import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { WEBSITE_PAGES_LOADING, WEBSITE_PAGE_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { PostGenerateAiHtmlRequest } from "./interfaces/api/post-generate-ai-html";
import { PostGenerateAiRequest } from "./interfaces/api/post-generate-ai";
import { PostWebsitePageRequest } from "./interfaces/api/post-website-page";
import { PutWebsitePageRequest } from "./interfaces/api/put-website-page";
import { WebsitePageContent } from "./interfaces/WebsiteBlock";
import { websitePageApiService } from "./services/api";
import {
  deleteWebsitePageSuccessAction,
  getWebsiteFormsSuccessAction,
  getWebsitePageFailureAction,
  getWebsitePageInitAction,
  getWebsitePageSuccessAction,
  getWebsitePagesFailureAction,
  getWebsitePagesInitAction,
  getWebsitePagesSuccessAction,
  postWebsitePageSuccessAction,
  putWebsitePageSuccessAction,
  setEditorContentAction,
  setEditorViewModeAction,
  setSelectedBlockAction,
} from "./store/api-actions";
import {
  editorContentSelector,
  editorViewModeSelector,
  selectedBlockIdSelector,
  websiteFormsSelector,
  websitePageLoadFailedSelector,
  websitePageSelector,
  websitePagesSelector,
} from "./store/selector";

const getApiErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

export function useWebsitePageCore() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { turnOnLoading, turnOffLoading } = useGlobalCore();
  const pages = useSelector(websitePagesSelector);
  const page = useSelector(websitePageSelector);
  const pageLoadFailed = useSelector(websitePageLoadFailedSelector);
  const forms = useSelector(websiteFormsSelector);
  const editorContent = useSelector(editorContentSelector);
  const selectedBlockId = useSelector(selectedBlockIdSelector);
  const viewMode = useSelector(editorViewModeSelector);

  const getPages = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getWebsitePagesInitAction());
        turnOnLoading(WEBSITE_PAGES_LOADING);
        const response = await websitePageApiService.getPages(companyId);
        dispatch(getWebsitePagesSuccessAction({ pages: response }));
        turnOffLoading(WEBSITE_PAGES_LOADING, 500);
      } catch (error) {
        turnOffLoading(WEBSITE_PAGES_LOADING);
        dispatch(getWebsitePagesFailureAction({ error }));
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.loadFailed" })
          )
        );
      }
    },
    [dispatch, intl, turnOnLoading, turnOffLoading]
  );

  const getPage = useCallback(
    async (companyId: string, pageId: string) => {
      try {
        dispatch(getWebsitePageInitAction());
        turnOnLoading(WEBSITE_PAGE_LOADING);
        const response = await websitePageApiService.getPage(companyId, pageId);
        dispatch(getWebsitePageSuccessAction({ page: response }));
        turnOffLoading(WEBSITE_PAGE_LOADING, 500);
      } catch (error) {
        turnOffLoading(WEBSITE_PAGE_LOADING);
        dispatch(getWebsitePageFailureAction({ error }));
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.loadPageFailed" })
          )
        );
      }
    },
    [dispatch, intl, turnOnLoading, turnOffLoading]
  );

  const getForms = useCallback(
    async (companyId: string) => {
      try {
        const response = await websitePageApiService.getForms(companyId);
        dispatch(getWebsiteFormsSuccessAction({ forms: response }));
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.websitePage.toast.formsLoadFailed" }));
      }
    },
    [dispatch, intl]
  );

  const createPage = useCallback(
    async (companyId: string, body: PostWebsitePageRequest) => {
      try {
        const response = await websitePageApiService.createPage(companyId, body);
        dispatch(postWebsitePageSuccessAction({ page: response }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.created" }));
        return response;
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.createFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const updatePage = useCallback(
    async (companyId: string, pageId: string, body: PutWebsitePageRequest) => {
      try {
        const response = await websitePageApiService.updatePage(companyId, pageId, body);
        dispatch(putWebsitePageSuccessAction({ page: response }));
        return response;
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.updateFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const savePage = useCallback(
    async (companyId: string, pageId: string, contentJson: WebsitePageContent, extra?: PutWebsitePageRequest) => {
      const response = await updatePage(companyId, pageId, { contentJson, ...extra });
      if (response.status === "published") {
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.republished" }));
      } else {
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.saved" }));
      }
      return response;
    },
    [updatePage, intl]
  );

  const deletePage = useCallback(
    async (companyId: string, pageId: string) => {
      try {
        await websitePageApiService.deletePage(companyId, pageId);
        dispatch(deleteWebsitePageSuccessAction({ pageId }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.deleted" }));
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.deleteFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const publishPage = useCallback(
    async (companyId: string, pageId: string) => {
      try {
        const response = await websitePageApiService.publishPage(companyId, pageId);
        dispatch(putWebsitePageSuccessAction({ page: response }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.published" }));
        return response;
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.publishFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const unpublishPage = useCallback(
    async (companyId: string, pageId: string) => {
      try {
        const response = await websitePageApiService.unpublishPage(companyId, pageId);
        dispatch(putWebsitePageSuccessAction({ page: response }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.unpublished" }));
        return response;
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.unpublishFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const updateDomain = useCallback(
    async (companyId: string, pageId: string, customDomain: string | null) => {
      try {
        const response = await websitePageApiService.updateDomain(companyId, pageId, { customDomain });
        dispatch(putWebsitePageSuccessAction({ page: response }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.domainSaved" }));
        return response;
      } catch (error: any) {
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.domainSaveFailed" })
          )
        );
        throw error;
      }
    },
    [dispatch, intl]
  );

  const verifyDomain = useCallback(
    async (companyId: string, pageId: string) => {
      try {
        const response = await websitePageApiService.verifyDomain(companyId, pageId);
        dispatch(putWebsitePageSuccessAction({ page: response }));
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.domainVerified" }));
        return response;
      } catch (error: any) {
        const message = error?.response?.data?.message || intl.formatMessage({ id: "app.websitePage.toast.domainVerifyFailed" });
        toast.error(message);
        throw error;
      }
    },
    [dispatch, intl]
  );

  const generateAi = useCallback(
    async (companyId: string, body: PostGenerateAiRequest) => {
      try {
        turnOnLoading(WEBSITE_PAGE_LOADING);
        const content = await websitePageApiService.generateAi(body, companyId);
        turnOffLoading(WEBSITE_PAGE_LOADING, 500);
        return content;
      } catch (error: any) {
        turnOffLoading(WEBSITE_PAGE_LOADING);
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.aiFailed" })
          )
        );
        throw error;
      }
    },
    [intl, turnOffLoading, turnOnLoading]
  );

  const generateAiHtml = useCallback(
    async (companyId: string, body: PostGenerateAiHtmlRequest) => {
      try {
        turnOnLoading(WEBSITE_PAGE_LOADING);
        const content = await websitePageApiService.generateAiHtml(body, companyId);
        turnOffLoading(WEBSITE_PAGE_LOADING, 500);
        return content;
      } catch (error: any) {
        turnOffLoading(WEBSITE_PAGE_LOADING);
        toast.error(
          getApiErrorMessage(
            error,
            intl.formatMessage({ id: "app.websitePage.toast.aiFailed" })
          )
        );
        throw error;
      }
    },
    [intl, turnOffLoading, turnOnLoading]
  );

  const getPublicPageHtml = useCallback(
    async (companyId: string, slug: string) => {
      return websitePageApiService.getPublicPageHtml(companyId, slug);
    },
    []
  );

  return {
    pages,
    page,
    pageLoadFailed,
    forms,
    editorContent,
    selectedBlockId,
    viewMode,
    getPages,
    getPage,
    getForms,
    createPage,
    updatePage,
    savePage,
    deletePage,
    publishPage,
    unpublishPage,
    updateDomain,
    verifyDomain,
    generateAi,
    generateAiHtml,
    getPublicPageHtml,
    setEditorContent: (contentJson: WebsitePageContent) =>
      dispatch(setEditorContentAction({ contentJson })),
    setSelectedBlock: (blockId: string | null) =>
      dispatch(setSelectedBlockAction({ blockId })),
    setViewMode: (mode: "desktop" | "tablet" | "mobile") =>
      dispatch(setEditorViewModeAction({ viewMode: mode })),
  };
}
